<!-- eslint-disable vue/no-mutating-props, vue/no-multiple-template-root, no-undef -->
<template>
  <div>
    <div class="dashboard-list-box fl-wrap activities">
      <div
        class="dashboard-header fl-wrap"
        style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px;"
      >
        <h3>My Ads</h3>
        <div
          class="sort-options"
          style="display: flex; gap: 10px;"
        >
          <button 
            class="btn sort-btn" 
            :class="{ 'active-sort': sortOption === 'recent' }"
            @click="sortOption = 'recent'"
          >
            Last Added
          </button>
          <button 
            class="btn sort-btn" 
            :class="{ 'active-sort': sortOption === 'viewed' }"
            @click="sortOption = 'viewed'"
          >
            Most Viewed
          </button>
        </div>
      </div>
      <!-- dashboard-list Start-->
      <div class="dashboard-list">
        <div class="user-ads-section">
          <!-- Ad tabs -->
          <div class="listing-tabs">
            <ul
              class="nav nav-tabs"
              role="tablist"
            >
              <li
                class="nav-item"
                role="presentation"
              >
                <button
                  class="nav-link"
                  :class="{ active: listingType === 'active' }"
                  role="tab"
                  @click="$emit('fetch-listings', 'active')"
                >
                  Active Listings
                </button>
              </li>
              <li
                class="nav-item"
                role="presentation"
              >
                <button
                  class="nav-link"
                  :class="{ active: listingType === 'archived' }"
                  role="tab"
                  @click="$emit('fetch-listings', 'archived')"
                >
                  Archived Listings
                </button>
              </li>
              <li
                class="nav-item"
                role="presentation"
              >
                <button
                  class="nav-link"
                  :class="{ active: listingType === 'draft' }"
                  role="tab"
                  @click="$emit('fetch-listings', 'draft')"
                >
                  Drafts
                </button>
              </li>
            </ul>
          </div>

          <!-- Loading indicator -->
          <div
            v-if="listingsLoading"
            class="listings-loading"
          >
            <LoadingSpinner />
            <p>Loading your listings...</p>
          </div>

          <!-- No listings message -->
          <div
            v-else-if="!listingsLoading && userListings.length === 0"
            class="no-listings"
          >
            <div class="empty-state">
              <i class="fas fa-list" />
              <h4>No listings found</h4>
              <p>You don't have any {{ listingType }} listings yet.</p>
              <router-link
                to="/add-listing"
                class="btn color-bg"
              >
                Create New Listing
              </router-link>
            </div>
          </div>

          <!-- Listings grid -->
          <div
            v-else
            class="row listing-grid"
          >
            <div
              v-for="listing in sortedListings"
              :key="listing.id"
              class="coledit col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-6"
            >
              <div class="listing-card">
                <div class="listing-img">
                  <img
                    :src="listing.coverImage || defaultImage"
                    alt="Listing Image"
                  >
                  <span class="listing-price">£{{ listing.price }}</span>
                  <span class="listing-category">{{ listing.category }}</span>
                </div>
                <div class="listing-content">
                  <h4 class="listing-title">
                    {{ truncateText(listing.title, 50) }}
                  </h4>
                  <p class="listing-desc">
                    {{ truncateDescription(listing.description) }}
                  </p>
                  <div class="listing-meta">
                    <span><i class="fas fa-eye" /> {{ listing.viewed || 0 }}</span>
                    <span><i class="fas fa-calendar" />
                      {{ formatDate(listing.createdAt) }}</span>
                  </div>
                  <div class="listing-actions">
                    <button
                      v-if="listingType === 'active' || listingType === 'draft'"
                      class="btn btn-edit"
                      @click="showEditModal(listing)"
                    >
                      <i class="fas fa-edit" /> {{ listingType === 'draft' ? 'Resume' : 'Edit' }}
                    </button>
                    <button
                      v-if="listingType === 'active'"
                      class="btn btn-archive"
                      @click="showArchiveModal(listing)"
                    >
                      <i class="fas fa-archive" /> Archive
                    </button>
                    <button
                      v-if="listingType === 'archived'"
                      class="btn btn-restore"
                      @click="showRestoreModal(listing)"
                    >
                      <i class="fas fa-trash-restore" /> Restore
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Archive Modal -->
        <div
          v-if="showArchiveConfirmModal"
          class="modal-overlay"
        >
          <div class="modal-content">
            <h4>Archive Listing</h4>
            <p>Are you sure you want to archive this listing?</p>
            <p>Archived listings will no longer be visible to other users.</p>
            <div v-if="archiveLoading">
              <LoadingSpinner />
              <p>Processing...</p>
            </div>
            <div
              v-else
              class="modal-actions"
            >
              <button
                class="btn btn-cancel"
                @click="cancelArchiveModal"
              >
                Cancel
              </button>
              <button
                class="btn color-bg"
                @click="confirmArchiveListing"
              >
                Yes, Archive
              </button>
            </div>
          </div>
        </div>

        <!-- Restore Modal -->
        <div
          v-if="showRestoreConfirmModal"
          class="modal-overlay"
        >
          <div class="modal-content">
            <h4>Restore Listing</h4>
            <p>Are you sure you want to restore this listing?</p>
            <p>Restored listings will be visible to users again.</p>
            <div v-if="restoreLoading">
              <LoadingSpinner />
              <p>Processing...</p>
            </div>
            <div
              v-else
              class="modal-actions"
            >
              <button
                class="btn btn-cancel"
                @click="cancelRestoreModal"
              >
                Cancel
              </button>
              <button
                class="btn color-bg"
                @click="confirmRestoreListing"
              >
                Yes, Restore
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- dashboard-list end-->
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import { updatePetListing } from '@/services/petService';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const props = defineProps({
  userListings: {
    type: Array,
    required: true
  },
  listingsLoading: {
    type: Boolean,
    required: true
  },
  listingType: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['fetch-listings']);
const router = useRouter();

const defaultImage = 'https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2Fno-image.jpg?alt=media';

// Sorting functionality
const sortOption = ref('recent'); // 'recent' or 'viewed'

const sortedListings = computed(() => {
  if (!props.userListings || props.userListings.length === 0) return [];
  
  const listings = [...props.userListings];
  
  if (sortOption.value === 'recent') {
    return listings.sort((a, b) => {
      let dateA = 0;
      let dateB = 0;
      if (a.createdAt) {
        dateA = a.createdAt.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt).getTime();
      }
      if (b.createdAt) {
        dateB = b.createdAt.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt).getTime();
      }
      return dateB - dateA;
    });
  } else if (sortOption.value === 'viewed') {
    return listings.sort((a, b) => {
      const viewA = a.viewed || 0;
      const viewB = b.viewed || 0;
      return viewB - viewA; // Descending order
    });
  }
  
  return listings;
});

// Edit modal state
const selectedListing = ref(null);

// Archive modal state
const showArchiveConfirmModal = ref(false);
const archiveLoading = ref(false);

// Restore modal state
const showRestoreConfirmModal = ref(false);
const restoreLoading = ref(false);

const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

const truncateDescription = (text) => {
  if (!text) return '';
  return text.length > 50 ? `${text.substring(0, 50)}...` : text;
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  try {
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  } catch (err) {
    console.error('Date formatting error:', err);
    return 'N/A';
  }
};

// Edit modal functions
const showEditModal = (listing) => {
  router.push({ path: `/edit-listing/${listing.id}` });
};

// Archive modal functions
const showArchiveModal = (listing) => {
  selectedListing.value = listing;
  showArchiveConfirmModal.value = true;
};

const cancelArchiveModal = () => {
  showArchiveConfirmModal.value = false;
  selectedListing.value = null;
};

const confirmArchiveListing = async () => {
  if (!selectedListing.value) return;

  try {
    archiveLoading.value = true;
    await updatePetListing(selectedListing.value.id, {
      status: 'archived',
      archivedAt: new Date(),
    });

    emit('fetch-listings', 'active');
    showArchiveConfirmModal.value = false;
    selectedListing.value = null;
  } catch (error) {
    console.error('Error archiving listing:', error);
  } finally {
    archiveLoading.value = false;
  }
};

// Restore modal functions
const showRestoreModal = (listing) => {
  selectedListing.value = listing;
  showRestoreConfirmModal.value = true;
};

const cancelRestoreModal = () => {
  showRestoreConfirmModal.value = false;
  selectedListing.value = null;
};

const confirmRestoreListing = async () => {
  if (!selectedListing.value) return;

  try {
    restoreLoading.value = true;
    await updatePetListing(selectedListing.value.id, {
      status: 'active',
      restoredAt: new Date(),
    });

    emit('fetch-listings', 'archived');
    showRestoreConfirmModal.value = false;
    selectedListing.value = null;
  } catch (error) {
    console.error('Error restoring listing:', error);
  } finally {
    restoreLoading.value = false;
  }
};
</script>

<style scoped>
/* Scoped styles will be applied by ProfilePage if we don't copy them here, 
   but it's better to copy the listing specific styles here if needed.
   For now, we rely on the parent CSS. */
</style>
<style scoped>
  .main_box {
    max-width: 100%;
    display: flex;
  }

  .gallery-edit {
    background: rgba(73, 206, 255, 0.8);
    color: #fff;
    width: 30px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    border-radius: 100%;
    display: inline-block;
    margin-right: 5px;
  }
  .gallery-items {
    margin-top: 20px;
  }

  .gallery-item {
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 20px;
  }

  .gallery-item img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 6px;
  }

  .gallery-item-controls {
    position: absolute;
    bottom: 10px;
    right: 10px;
    opacity: 0;
    transition: opacity 0.3s;
  }

  .gallery-item:hover .gallery-item-controls {
    opacity: 1;
  }

  .gallery-delete {
    background: rgba(255, 59, 48, 0.8);
    color: #fff;
    width: 30px;
    height: 30px;
    line-height: 30px;
    text-align: center;
    border-radius: 100%;
    display: inline-block;
  }

  .fuzone.is-loading {
    opacity: 0.7;
    pointer-events: none;
  }

  .fw-opt {
    float: right;
    font-size: 12px;
    color: #999;
  }

  .photoUpload.is-loading {
    opacity: 0.7;
    pointer-events: none;
  }

  .upload-error {
    color: #ff3b30;
    font-size: 12px;
    margin-top: 5px;
    text-align: center;
  }
  .menu-open {
    cursor: pointer;
    position: fixed;
    left: -35px;
    right: unset;
    z-index: 999;
    color: #000000;
    right: unset !important;
    bottom: unset !important;
    transform: rotate(270deg);
  }

  .menu-close {
    cursor: pointer;
    color: #000000;
  }

  .animate__animated {
    --animate-duration: 0.6s;
  }

  /* ortak CSS*/
  ul,
  li {
    border: none;
    outline: 0;
    font-weight: inherit;
    font-style: inherit;
    font-size: 100%;
    font-family: inherit;
    vertical-align: baseline;
    text-decoration: none;
    margin: 0;
    padding: 0;
  }

  .row {
    margin-left: -15px;
    margin-right: -15px;
  }

  .clearfix:before,
  .clearfix:after,
  .container:before,
  .container:after,
  .container-fluid:before,
  .container-fluid:after,
  .row:before,
  .row:after {
    content: ' ';
    display: table;
  }

  .fl-wrap {
    float: left;
    width: 100%;
    position: relative;
  }

  .gradient-bg,
  .listing-geodir-category,
  .list-single-header-cat a,
  .box-widget-item .list-single-tags a:hover,
  .nav-holder nav li a:before {
    background-image: -webkit-gradient(linear, 0% 0%, 0% 100%, from(#f8fbfe), to(#fef8f9ff));
    background-position-x: initial;
    background-position-y: initial;
    background-size: initial;
    background-repeat: initial;
    background-attachment: initial;
    background-origin: initial;
    background-clip: initial;
    background-color: initial;
    background-color: #3eaafd;
    background: -webkit-gradient(linear, 0% 0%, 0% 100%, from(#3eaafd), to(#fef8f9ff));
    background: -webkit-linear-gradient(top, #f8fbfe, #fef8f9ff);
    background: -moz-linear-gradient(top, #f8fbfe, #fef8f9ff);
    background: -ms-linear-gradient(top, #f8fbfe, #fef8f9ff);
    background: -o-linear-gradient(top, #f8fbfe, #fef8f9ff);
  }

  .pushtop {
    min-height: 80px;
  }

  p {
    font-size: 12px;
    line-height: 24px;
    padding-bottom: 10px;
    font-weight: 500;
    color: #878c9f;
  }

  .profile-edit-page-header {
    float: left;
    width: 100%;
    position: relative;
    padding: 15px 30px;
    border-radius: 6px;
    border: 1px solid #eee;
    margin-bottom: 30px;
    background: #f8fbfe;
  }

  .profile-edit-page-header h2 {
    text-align: left;
    font-size: 26px;
    color: #334e6f;
    font-weight: 500;
    float: left;
  }

  .profile-edit-page-header .breadcrumbs {
    float: right;
    position: relative;
    top: 8px;
  }

  .fixed-bar {
    z-index: auto;
    position: relative;
    top: 0px;

    float: left;
    width: 100%;
  }

  .user-profile-menu-wrap {
    padding: 15px 20px;
    background: #f8fbfe;
    border: 1px solid #eee;
    border-radius: 4px;
    height: 100%;
  }

  .user-profile-menu-wrap:before {
    top: 20px;
    right: 20px;
    font-size: 33px;
    opacity: 0.3;
    -webkit-animation: spinnerRotate 9s infinitelinear;
    -moz-animation: spinnerRotate 9s infinite linear;
    -o-animation: spinnerRotate 9s infinite linear;
    animation: spinnerRotate 9s infinitelinear;
  }

  .user-profile-menu {
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    float: left;
    width: 100%;
  }

  .user-profile-menu h3 {
    text-align: left;
    font-size: 16px;
    padding-bottom: 20px;
    font-weight: 600;
    color: #98aab8;
  }

  .user-profile-menu li {
    width: 100%;
    margin-bottom: 14px;
    text-align: left;
    float: left;
  }

  .user-profile-menu li a {
    font-weight: 600;
    color: #50596e;
    position: relative;
    min-width: 90px;
    float: left;
    padding: 5px;
  }

  .user-profile-menu li a:hover i,
  .user-profile-menu li a.user-profile-act i {
    opacity: 1;
  }

  .user-profile-menu li a i {
    padding-right: 25px;
    width: 20px;
    float: left;
    text-align: center;
    opacity: 0.4;
    position: relative;
    top: 2px;
  }

  .user-profile-menu li a span {
    position: absolute;
    top: 50%;
    right: -30px;
    width: 20px;
    height: 20px;
    margin-top: -10px;
    line-height: 20px;
    background: #98aab8;
    color: #fff;
    border-radius: 100%;
    text-align: center;
    font-weight: 300px;
    font-size: 10px;
    background: #4db7fe;
  }

  .log-out-btn {
    float: left;
    padding: 12px 35px;
    border: 2px solid;
    background: #daf4ff;
    font-weight: 600;
    font-size: 12px;
    border-radius: 6px;
    border-color: #4db7fe;
    color: #4db7fe;
  }

  @media (min-width: 1064px) {
    .col-md-1,
    .col-md-2,
    .col-md-3,
    .col-md-4,
    .col-md-5,
    .col-md-6,
    .col-md-7,
    .col-md-8,
    .col-md-9,
    .col-md-10,
    .col-md-11,
    .col-md-12 {
      float: left;
    }

    .col-md-2 {
      width: 16.66666667%;
    }

    .col-md-3 {
      width: 25%;
    }

    .col-md-4 {
      width: 33.33333333%;
    }

    .col-md-7 {
      width: 58.33333333%;
    }

    .col-md-9 {
      width: 75%;
    }
  }

  .col-xs-1,
  .col-sm-1,
  .col-md-1,
  .col-lg-1,
  .col-xs-2,
  .col-sm-2,
  .col-md-2,
  .col-lg-2,
  .col-xs-3,
  .col-sm-3,
  .col-md-3,
  .col-lg-3,
  .col-xs-4,
  .col-sm-4,
  .col-md-4,
  .col-lg-4,
  .col-xs-5,
  .col-sm-5,
  .col-md-5,
  .col-lg-5,
  .col-xs-6,
  .col-sm-6,
  .col-md-6,
  .col-lg-6,
  .col-xs-7,
  .col-sm-7,
  .col-md-7,
  .col-lg-7,
  .col-xs-8,
  .col-sm-8,
  .col-md-8,
  .col-lg-8,
  .col-xs-9,
  .col-sm-9,
  .col-md-9,
  .col-lg-9,
  .col-xs-10,
  .col-sm-10,
  .col-md-10,
  .col-lg-10,
  .col-xs-11,
  .col-sm-11,
  .col-md-11,
  .col-lg-11,
  .col-xs-12,
  .col-sm-12,
  .col-md-12,
  .col-lg-12 {
    position: relative;
    min-height: 1px;
    padding-left: 15px;
    padding-right: 15px;
  }

  /* profil bolumu*/
  .statistic-container {
    margin-bottom: 20px;
  }

  .statistic-item-wrap {
    float: left;
    width: 25%;
    padding: 0 10px;
    margin-bottom: 20px;
  }

  .statistic-item i {
    font-size: 74px;
    position: absolute;
    bottom: 40px;
    right: 20px;
    color: rgba(255, 255, 255, 0.41);
  }

  .statistic-item-numder {
    font-weight: 800;
    font-size: 34px;
    padding-bottom: 10px;
  }

  .statistic-item h5,
  .statistic-item-numder {
    float: left;
    width: 100%;
    text-align: left;
    color: #431d4b;
  }

  .statistic-item {
    padding: 20px 30px;
    border-radius: 6px;
  }

  .dashboard-list-box {
    border: 1px solid #eee;
    border-radius: 10px;
  }

  .dashboard-header {
    padding: 20px 30px;
    border-bottom: 1px solid #eee;
    background: #f8fbfe;
  }

  .dashboard-header h3 {
    font-size: 16px;
    font-weight: 600;
    text-align: left;
  }

  .dashboard-list {
    float: left;
    padding: 25px 30px;
    position: relative;
    border-bottom: 1px solid #eee;
    width: 100%;
  }

  .dashboard-list-box.activities .new-dashboard-item {
    cursor: pointer;
  }

  .new-dashboard-item {
    position: absolute;
    right: 30px;
    top: 22px;
    padding: 6px 15px;
    border-radius: 4px;
    color: #fff;
    background: #5ecfb1;
    z-index: 10;
  }

  .dashboard-list-box.activities .dashboard-message-text {
    padding-left: 0;
  }

  .dashboard-message-text {
    margin-left: 30px;
    text-align: left;
    padding-left: 50px;
  }

  .dashboard-list-box.activities .dashboard-message-text i {
    padding-right: 20px;
    color: #4db7fe;
  }

  /* profil edit*/
  .profile-edit-header {
    float: left;
    width: 100%;
    position: relative;
    padding-bottom: 30px;
  }

  .profile-edit-header h4 {
    text-align: left;
    font-size: 18px;
    color: #98aab8;
    font-weight: 700;
    padding-bottom: 20px;
  }

  .custom-form {
    float: left;
    width: 100%;
    position: relative;
  }

  .profile-edit-container .custom-form label {
    float: left;
    text-align: left;
    margin-bottom: 10px;
    font-weight: 500;
  }

  .custom-form label {
    float: left;
    position: relative;
    width: 100%;
    text-align: left;
    font-weight: 500;
    color: #666;
  }

  .profile-edit-container .custom-form label i {
    top: 54px;
  }

  .custom-form label i {
    padding-right: 12px;
    font-size: 14px;
    position: absolute;
    top: 16px;
    left: 20px;
    color: #4db7fe;
  }

  .custom-form textarea,
  .custom-form input[type='text'],
  .custom-form input[type='email'],
  .custom-form input[type='password'],
  .custom-form input[type='button'] {
    float: left;
    border: 1px solid #e5e7f2;
    background: #f9f9f9;
    width: 100%;
    padding: 12px 20px 12px 45px;
    border-radius: 4px;
    color: #7d93b2;
    font-size: 12px;
    appearance: none;
    outline: none;
    overflow: hidden;
    z-index: 1;
  }
  .custom-form textarea:focus,
  .custom-form input[type='text']:focus,
  .custom-form input[type='email']:focus,
  .custom-form input[type='password']:focus,
  .listsearch-input-item input[type='text']:focus {
    background: #fff;
    box-shadow: 0px 10px 14px 0px rgba(12, 0, 46, 0.06);
  }
  .custom-form input {
    margin-bottom: 20px;
    border-radius: 3px;
    border: 1px solid #eee;
    margin: 3px;
  }

  .custom-form textarea {
    height: 200px;
    resize: none;
    padding: 25px 20px;
    appearance: none;
  }

  .profile-edit-container.add-list-container {
    margin-top: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #eee;
    float: left;
    width: 100%;
  }

  .profile-edit-header {
    float: left;
    width: 100%;
    position: relative;
    padding-bottom: 30px;
  }

  .profile-edit-header h4 {
    text-align: left;
    font-size: 18px;
    color: #98aab8;
    font-weight: 700;
    padding-bottom: 20px;
  }

  .add-list-media-header {
    float: left;
    width: 100%;
    min-width: 226px;
    padding: 15px 20px 3px;
    background: #f8fbfe;
    border: 1px solid #eee;
  }

  .add-list-media-header.member {
    float: left;
    width: 100%;
    padding: 15px 20px 3px;
    background: #f8fbfe;
    border: 1px solid #713d7150;
  }

  .profile-edit-container .custom-form label {
    float: left;
    text-align: left;
    margin-bottom: 10px;
    font-weight: 500;
  }

  .custom-form label {
    float: left;
    position: relative;
    width: 100%;
    text-align: left;
    font-weight: 500;
    color: #666;
  }

  .radio {
    color: #999;
    font-size: 13px;
    position: relative;
    top: -2px;
  }

  .radio input[type='radio'] {
    cursor: pointer;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
    opacity: 0;
  }

  .custom-form input {
    margin-bottom: 20px;
  }

  .radio span {
    position: relative;
    padding-left: 30px;
  }

  .add-list-media-header span {
    text-align: left;
    float: left;
  }
  .mship-pln:has(input:checked) {
    background: #ffeef5;

    border-color: #4db7fe;
  }
  .radio span:after {
    content: '';
    width: 20px;
    height: 20px;
    border: 1px solid #ccc;
    position: absolute;
    left: 0;
    top: -1px;
    border-radius: 3px;
    box-sizing: border-box;
  }

  .radio input[type='radio']:checked + span:before {
    font-family: FontAwesome;
    font-style: normal;
    font-weight: normal;
    text-decoration: inherit;
    content: '\f00c';
    font-size: 17px;
    position: absolute;
    top: -2px;
    left: 3px;
    z-index: 20;
    color: #4db7fe;
  }

  .respimg {
    width: 100%;
    height: auto;
  }

  img {
    border: 0;
    -ms-interpolation-mode: bicubic;
    vertical-align: middle;
  }

  .change-photo-btn {
    margin-top: 20px;
  }

  .photoUpload {
    position: relative;
    padding: 15px 30px;
    color: #fff;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    background: #4db7fe;
    text-align: center;
  }

  .photoUpload input.upload {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    cursor: pointer;
    opacity: 0;
  }

  /*company edit*/
  .profile-edit-container .add-list-media-header {
    width: unset;
  }

  .filter-tags input,
  .custom-form .filter-tags input {
    float: left;
    position: relative;
    border: 1px solid #ccc;
    cursor: pointer;
    padding: 0;
    width: 20px;
    height: 20px;
    position: relative;
    color: #fff;
    background: #fff !important;
    appearance: none;
    margin-bottom: 0px;
  }

  .custom-form input {
    margin-bottom: 20px;
  }

  .filter-tags .add-list-media-header {
    padding: 10px 15px 5px;
  }

  .filter-tags label,
  .custom-form .filter-tags label {
    float: left;
    padding: 0 10px;
    position: relative;
    top: 0;
    margin-bottom: 0;
    color: #888da0;
    font-weight: 600;
    width: auto;
  }

  .radio input[type='radio'] {
    cursor: pointer;
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1;
    opacity: 0;
  }

  .custom-form input {
    margin-bottom: 20px;
  }

  .filter-tags input:checked:after,
  .custom-form .filter-tags input:checked:after {
    font-family: FontAwesome;
    font-style: normal;
    font-weight: normal;
    text-decoration: inherit;
    content: '\f00c';
    font-size: 16px;
    position: absolute;
    top: -2px;
    left: 3px;
    z-index: 20;
    color: #4db7fe;
  }

  .header-user-name span {
    position: absolute;
    width: 38px;
    height: 38px;
    border-radius: 100%;
    overflow: hidden;
    top: -10px;
    left: -50px;
  }

  .profile-edit-container.add-list-container {
    margin-top: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #eee;
    float: left;
    width: 100%;
  }

  .fuzone {
    position: relative;
    border: 1px solid #eee;
    border-radius: 3px;
    background: #f7f9fb;
    transition: all 0.3slinear;
    margin-bottom: 10px;
    display: block;
    width: 100%;
    min-height: 160px;
    margin-top: 0px;
    cursor: pointer;
  }

  .fuzone .fu-text {
    text-align: center;
    margin: 50px 0;
    font-size: 12px;
    color: #999;
    font-weight: 400;
    position: relative;
  }

  .fuzone .fu-text i {
    font-size: 44px;
    width: 100%;
    padding-bottom: 10px;
    transition: all 0.3slinear;
    color: #666;
  }

  .fuzone input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    z-index: 100;
    cursor: pointer;
  }

  .custom-form input {
    margin-bottom: 20px;
  }

  .fuzone:hover .fu-text i {
    transform: translateY(-10px);
  }

  .filter-tags {
    margin-bottom: 20px;
  }
  .mship-pln {
    width: 100% !important;
  }
  @media screen and (max-width: 492px) {
    .ns-box.ns-bar {
      top: 0 !important;
      left: 0;
      width: 100%;
    }
    .pushtop {
      height: 20px !important;
      min-height: 20px !important;
    }
    .profile-edit-page-header {
      margin-bottom: 10px;
      padding: 10px 15px;
    }
    .statistic-item-wrap {
      width: 33%;
    }
    .statistic-item {
      padding: 5px 12px;
    }
    .statistic-item i {
      bottom: 5px;
      color: rgb(255 212 212 / 41%);
      font-size: 40px;
    }
    .statistic-item-numder {
      font-size: 25px;
    }
    .statistic-item h5 {
      font-size: smaller;
    }
    .dashboard-list {
      padding: 10px 5px;
    }
    .edit-profile-photo i {
      display: none;
    }
    .edit-profile-photo .photoUpload {
      padding: 5px 10px;
      font-size: 10px;
    }
    .mobil-fix {
      position: fixed !important;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 10000;
      padding: 0;
    }
    .menu-open {
      top: 200px !important;
      padding: 5px 10px !important;
    }
    .filter-tags .add-list-media-header {
      padding: 5px 4px 5px;
    }

    .col-md-9 {
      padding-left: 10px;
      padding-right: 10px;
    }

    .add-list-media-header {
      min-width: 200px;
      font-size: small;
    }
  }
  /* Posta kodu kaydetme butonu stilleri */
  .save-postal-btn {
    background-color: #fff !important;
    color: #c5322d !important;
    border: 2px solid #c5322d !important;
    box-shadow: 0 0 10px rgba(197, 50, 45, 0.3);
    animation: pulse 1.5s infinite;
    position: relative;
  }

  .save-postal-btn:hover {
    background-color: #c5322d !important;
    color: #fff !important;
    animation: none;
  }

  .save-postal-btn:disabled {
    background-color: #f8f9fa !important;
    color: #6c757d !important;
    border-color: #6c757d !important;
    animation: none;
    box-shadow: none;
  }

  /* Tooltip stilleri */
  .save-postal-btn[data-tooltip] {
    position: relative;
  }

  .save-postal-btn[data-tooltip]:before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 0.3s,
      visibility 0.3s;
    margin-bottom: 5px;
    z-index: 10;
  }

  .save-postal-btn[data-tooltip]:after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
    visibility: hidden;
    opacity: 0;
    transition:
      opacity 0.3s,
      visibility 0.3s;
    margin-bottom: -5px;
    z-index: 9;
  }

  .save-postal-btn[data-tooltip]:hover:before,
  .save-postal-btn[data-tooltip]:hover:after {
    visibility: visible;
    opacity: 1;
  }

  /* Pulsing animation */
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(197, 50, 45, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(197, 50, 45, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(197, 50, 45, 0);
    }
  }

  /* Başarılı kayıt mesajı */
  .postal-success {
    color: #28a745;
    font-weight: 500;
    display: inline-block;
    padding: 5px 10px;
    background-color: rgba(40, 167, 69, 0.1);
    border-radius: 4px;
    margin-top: 5px;
    animation: fadeIn 0.5s;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Ads section styles - style scoped içine ekleyin */
  .listing-tabs {
    margin-bottom: 20px;
  }

  .listing-tabs .nav {
    display: flex;
    gap: 10px;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
    justify-content: space-evenly;
  }

  .listing-tabs .nav-link {
    background: none;
    border: none;
    color: #666;
    font-weight: 600;
    padding: 8px 15px;
    border-radius: 4px;
    cursor: pointer;
  }

  .listing-tabs .nav-link.active {
    border-radius: 6px;
    border: 1px solid #eee;
    margin-bottom: 30px;
    background: #f8fbfe;
    color: #431d4b;
  }

  .listing-card {
    border: 1px solid #eee;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition:
      transform 0.3s,
      box-shadow 0.3s;
  }

  .listing-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  }

  .listing-img {
    position: relative;
    height: 200px;
    overflow: hidden;
  }

  .listing-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .listing-price {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #4db7fe;
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-weight: bold;
  }

  .listing-category {
    position: absolute;
    bottom: 15px;
    left: 15px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
  }

  .listing-content {
    padding: 15px;
  }

  .listing-title {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 600;
    color: #334e6f;
  }

  .listing-desc {
    margin: 0 0 15px;
    font-size: 13px;
    color: #666;
    height: 60px;
    overflow: hidden;
  }

  .listing-meta {
    border-top: 1px solid #eee;
    padding-top: 10px;
    margin-bottom: 15px;
  }

  .listing-meta span {
    font-size: 12px;
    color: #888;
    margin-right: 15px;
  }

  .listing-meta span i {
    margin-right: 5px;
    color: #4db7fe;
  }

  .listing-actions {
    display: flex;
    gap: 10px;
  }

  .btn-edit {
    background: #b8aee8;
    color: white;
  }

  .btn-archive {
    background: #d96e79;
    color: white;
  }

  .btn-restore {
    background: #5ecfb1;
    color: white;
  }

  .btn {
    padding: 8px 15px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
    transition: background 0.3s;
  }

  .btn:hover {
    opacity: 0.9;
  }

  .btn-cancel {
    background: #f1f1f1;
    color: #666;
  }

  /* Modal styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 6px;
    padding: 30px;
    width: 90%;
    max-width: 500px;
    text-align: center;
  }

  .modal-content h4 {
    color: #334e6f;
    margin-bottom: 20px;
  }

  .modal-actions {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 25px;
  }

  .empty-state {
    text-align: center;
    padding: 40px 20px;
  }

  .empty-state i {
    font-size: 48px;
    color: #ccc;
    margin-bottom: 20px;
  }

  .empty-state h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.sort-btn {
  background: #ffffff;
  color: #666;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.sort-btn:hover {
  transform: translateY(-2px);
  background: #f8fafc;
  border-color: #cbd5e1;
}
.sort-btn.active-sort {
  background: #334e6f;
  border-color: #334e6f;
  box-shadow: 0 6px 12px rgba(51, 78, 111, 0.3);
  transform: translateY(-2px);
  color: #ffffff !important;
}

  .empty-state p {
    color: #888;
    margin-bottom: 25px;
  }

  .listings-loading {
    text-align: center;
    padding: 30px 0;
  }
  .coledit {
    padding-left: 5px;
    padding-right: 5px;
  }
  /* To bar notifications */
  /* Common, default styles for the notification box */

  .ns-box {
    position: fixed;
    background-color: rgba(239, 148, 0, 0.2);
    border: 2px solid #ef9400;
    padding: 22px;
    line-height: 1.4;
    z-index: 1000;
    pointer-events: none;
    color: rgba(250, 251, 255, 0.95);
    font-size: 90%;
    font-family: 'Helvetica Neue', 'Segoe UI', Helvetica, Arial, sans-serif;
  }

  .ns-box.ns-show {
    pointer-events: auto;
  }

  .ns-box a {
    color: inherit;
    opacity: 0.7;
    font-weight: 700;
  }

  .ns-box a:hover,
  .ns-box a:focus {
    opacity: 1;
  }

  .ns-box p {
    margin: 0;
  }

  .ns-box.ns-show,
  .ns-box.ns-visible {
    pointer-events: auto;
  }

  .ns-close {
    width: 20px;
    height: 20px;
    position: absolute;
    right: 4px;
    top: 4px;
    overflow: hidden;
    text-indent: 100%;
    cursor: pointer;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  .ns-close:hover,
  .ns-close:focus {
    outline: none;
  }

  .ns-close::before,
  .ns-close::after {
    content: '';
    position: absolute;
    width: 3px;
    height: 60%;
    top: 50%;
    left: 50%;
    background: #6e6e6e;
  }

  .ns-close:hover::before,
  .ns-close:hover::after {
    background: #fff;
  }

  .ns-close::before {
    -webkit-transform: translate(-50%, -50%) rotate(45deg);
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .ns-close::after {
    -webkit-transform: translate(-50%, -50%) rotate(-45deg);
    transform: translate(-50%, -50%) rotate(-45deg);
  }
  .ns-box.ns-bar {
    top: 50px;
    left: 0;
    width: 99%;
  }

  .ns-bar .ns-close {
    background: transparent;
    top: 50%;
    right: 20px;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
  }

  .ns-bar .ns-close::before,
  .ns-bar .ns-close::after {
    background: #b7b5b3;
  }

  [class^='ns-effect-'].ns-bar.ns-hide,
  [class*=' ns-effect-'].ns-bar.ns-hide {
    -webkit-animation-direction: reverse;
    animation-direction: reverse;
  }

  /* Individual Effects */

  /* Slide on top */
  .ns-effect-slidetop {
    box-shadow: inset 4.5em 0 rgba(0, 0, 0, 0.6);
  }

  .ns-effect-slidetop .icon {
    position: absolute;
    display: block;
    font-size: 36px;
    top: 50%;
    left: 0.3em;
    -webkit-transform: translate3d(0, -50%, 0);
    transform: translate3d(0, -50%, 0);
  }

  .ns-effect-slidetop p {
    padding: 0 3.2em;
    font-size: 1.2em;
    display: inline-block;
  }

  .ns-effect-slidetop .ns-close::before,
  .ns-effect-slidetop .ns-close::after {
    width: 2px;
    background: #14151b;
  }

  .ns-effect-slidetop .ns-close:hover::before,
  .ns-effect-slidetop .ns-close:hover::after {
    background: #fff;
  }

  .ns-effect-slidetop.ns-show .icon,
  .ns-effect-slidetop.ns-show p {
    -webkit-animation-name: animScaleUp;
    animation-name: animScaleUp;
    -webkit-animation-duration: 0.3s;
    animation-duration: 0.3s;
    -webkit-animation-delay: 0.2s;
    animation-delay: 0.2s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
  }

  .ns-effect-slidetop.ns-show p {
    -webkit-animation-name: animFade;
    animation-name: animFade;
  }

  @-webkit-keyframes animScaleUp {
    0% {
      opacity: 0;
      -webkit-transform: translate3d(0, -50%, 0) scale3d(0, 0, 1);
    }
    100% {
      opacity: 1;
      -webkit-transform: translate3d(0, -50%, 0) scale3d(1, 1, 1);
    }
  }

  @keyframes animScaleUp {
    0% {
      opacity: 0;
      -webkit-transform: translate3d(0, -50%, 0) scale3d(0, 0, 1);
      transform: translate3d(0, -50%, 0) scale3d(0, 0, 1);
    }
    100% {
      opacity: 1;
      -webkit-transform: translate3d(0, -50%, 0) scale3d(1, 1, 1);
      transform: translate3d(0, -50%, 0) scale3d(1, 1, 1);
    }
  }

  .ns-effect-slidetop.ns-show,
  .ns-effect-slidetop.ns-hide {
    -webkit-animation-name: animSlideTop;
    animation-name: animSlideTop;
    -webkit-animation-duration: 0.3s;
    animation-duration: 0.3s;
    -webkit-animation-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
    animation-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
  }

  @-webkit-keyframes animSlideTop {
    0% {
      -webkit-transform: translate3d(0, -100%, 0);
    }
    100% {
      -webkit-transform: translate3d(0, 0, 0);
    }
  }

  @keyframes animSlideTop {
    0% {
      -webkit-transform: translate3d(0, -100%, 0);
      transform: translate3d(0, -100%, 0);
    }
    100% {
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
    }
  }

  /* Expanding Loader */
  .ns-effect-exploader {
    background: #fff;
    color: #7a7264;
    -webkit-transform-origin: 0 0;
    transform-origin: 0 0;
  }

  .ns-effect-exploader .icon {
    position: absolute;
    left: 0.25em;
    top: 50%;
    -webkit-transform: translate3d(0, -50%, 0);
    transform: translate3d(0, -50%, 0);
  }

  .ns-effect-exploader .ns-box-inner {
    position: relative;
  }

  .ns-effect-exploader p {
    padding: 0.25em 2em 0.25em 3em;
  }

  .ns-effect-exploader .ns-close:hover::before,
  .ns-effect-exploader .ns-close:hover::after {
    background: #98815a;
  }

  .ns-effect-exploader.ns-show {
    -webkit-animation-name: animLoad;
    animation-name: animLoad;
    -webkit-animation-duration: 2.5s;
    animation-duration: 2.5s;
  }

  @-webkit-keyframes animLoad {
    0% {
      opacity: 1;
      -webkit-transform: scale3d(0, 0.3, 1);
    }
    33% {
      opacity: 1;
      -webkit-transform: scale3d(0.5, 0.3, 1);
    }
    50% {
      opacity: 1;
      -webkit-transform: scale3d(0.6, 0.3, 1);
    }
    80%,
    85% {
      opacity: 1;
      -webkit-transform: scale3d(1, 0.3, 1);
      -webkit-animation-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
    }
    100% {
      opacity: 1;
      -webkit-transform: scale3d(1, 1, 1);
    }
  }

  @keyframes animLoad {
    0% {
      opacity: 1;
      -webkit-transform: scale3d(0, 0.3, 1);
      transform: scale3d(0, 0.3, 1);
    }
    33% {
      opacity: 1;
      -webkit-transform: scale3d(0.5, 0.3, 1);
      transform: scale3d(0.5, 0.3, 1);
    }
    50% {
      opacity: 1;
      -webkit-transform: scale3d(0.6, 0.3, 1);
      transform: scale3d(0.6, 0.3, 1);
    }
    80%,
    85% {
      opacity: 1;
      -webkit-transform: scale3d(1, 0.3, 1);
      transform: scale3d(1, 0.3, 1);
      -webkit-animation-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
      animation-timing-function: cubic-bezier(0.7, 0, 0.3, 1);
    }
    100% {
      opacity: 1;
      -webkit-transform: scale3d(1, 1, 1);
      transform: scale3d(1, 1, 1);
    }
  }

  .ns-effect-exploader.ns-hide {
    -webkit-animation-name: animFade;
    animation-name: animFade;
    -webkit-animation-duration: 0.3s;
    animation-duration: 0.3s;
  }

  .ns-effect-exploader.ns-show .ns-box-inner,
  .ns-effect-exploader.ns-show .ns-close {
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
    -webkit-animation-duration: 0.3s;
    animation-duration: 0.3s;
    -webkit-animation-delay: 2.4s;
    animation-delay: 2.4s;
  }

  .ns-effect-exploader.ns-show .ns-close {
    -webkit-animation-name: animFade;
    animation-name: animFade;
  }

  .ns-effect-exploader.ns-show .ns-box-inner {
    -webkit-animation-name: animFadeMove;
    animation-name: animFadeMove;
    -webkit-animation-timing-function: ease-out;
    animation-timing-function: ease-out;
  }

  @-webkit-keyframes animFadeMove {
    0% {
      opacity: 0;
      -webkit-transform: translate3d(0, 10px, 0);
    }
    100% {
      opacity: 1;
      -webkit-transform: translate3d(0, 0, 0);
    }
  }

  @keyframes animFadeMove {
    0% {
      opacity: 0;
      -webkit-transform: translate3d(0, 10px, 0);
      transform: translate3d(0, 10px, 0);
    }
    100% {
      opacity: 1;
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
    }
  }

  @-webkit-keyframes animFade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @keyframes animFade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .pricing {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    margin: 0px auto 3em;
  }
  .pricing__item--featured {
    border-width: 1px;
    border-style: solid;
    border-color: rgb(207, 207, 207);
    border-image: initial;
    background: rgba(207, 207, 207, 0.18);
  }

  .pricing--norbu .pricing__item {
    color: rgb(199, 199, 199);
    cursor: default;
    font-family: 'Myriad Pro', Arial, sans-serif;
    margin: 0.5em;
    border-width: 1px;
    border-style: solid;
    border-color: rgba(199, 199, 199, 0.4);
    border-image: initial;
    background: rgba(199, 199, 199, 0.08);
    border-radius: 10px;
    transition:
      border-color 0.3s,
      background 0.3s;
  }
  .pricing--norbu .pricing__item:hover {
    border-width: 1px;
    border-style: solid;
    border-color: rgb(207, 207, 207);
    border-image: initial;
    background: rgba(207, 207, 207, 0.18);
  }
  .pricing__item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    flex: 0 1 330px;
  }
  .pricing--norbu .pricing__title {
    font-size: 2em;
    font-weight: 400;
    position: relative;
    margin: 0.5em 0px;
    padding: 1em;
  }
  .pricing--norbu .pricing__title::after {
    content: '';
    position: absolute;
    width: 20%;
    height: 1px;
    left: 40%;
    bottom: 0px;
    background: rgb(255, 255, 255);
  }
  .pricing--norbu .pricing__sentence {
    font-size: 1em;
    padding: 1em 2em;
    margin: 0px auto 1em;
  }
  .pricing--norbu .pricing__price {
    font-size: 3.5em;
    font-weight: 400;
    position: relative;
    z-index: 100;
    padding: 0.5em 0px 0px;
  }
  .pricing--norbu .pricing__currency {
    font-size: 0.5em;
    vertical-align: super;
  }
  .pricing--norbu .pricing__period {
    font-size: 0.25em;
    display: block;
    padding: 1em;
  }
  .pricing--norbu .pricing__feature-list {
    font-size: 1.15em;
    letter-spacing: 0px;
    margin: 0px 2em;
    padding: 2em 0px;
    list-style: none;
  }
  .pricing__feature-list {
    text-align: left;
  }
  .pricing--norbu .pricing__feature {
    line-height: 1.4;
  }
  .pricing--norbu .pricing__feature i::before {
    display: inline-block;
    vertical-align: middle;
    padding: 0px 0.75em 0px 0px;
  }
  .pricing--norbu .pricing__action {
    font-weight: bold;
    color: rgb(74, 168, 228);
    flex: 0 0 auto;
    margin: auto 1em 1em;
    padding: 1.25em 2em;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 5px;
    transition: background 0.3s;
  }
  .pricing__action {
    color: inherit;
    border-width: initial;
    border-style: none;
    border-color: initial;
    border-image: initial;
    background: none;
  }
</style>
